import {
  Animated,
  Image,
  Platform,
  Pressable,
  View,
  ScrollView,
  Text,
  StyleSheet,
  useWindowDimensions,
  type StyleProp,
  type TextProps,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { useEffect, useRef, useState } from "react";
import { BlurView } from "expo-blur";
import { ArrowRight } from "lucide-react-native";
import OnboardingShell from "./OnboardingShell";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { getOnboardingViewport } from "../onboardingViewport";

type MovingGradientTitleProps = TextProps & {
  className?: string;
};

type WebTextStyle = TextStyle & {
  backgroundClip?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundSize?: string;
  WebkitBackgroundClip?: string;
  WebkitTextFillColor?: string;
};

const WEB_GRADIENT_STYLE: WebTextStyle = {
  backgroundImage:
    "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 40%, rgba(212,165,116,0.9) 60%, rgba(255,255,255,0.8) 80%, rgba(255,255,255,1) 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  backgroundSize: "200% 200%",
};

const LOUISE_MARK = require("../../assets/images/louise-mark-white.png");

export const GLASS_CONTROL_SHELL_STYLE: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  minHeight: 58,
  borderRadius: 18,
  borderWidth: 1,
  borderColor: "rgba(255, 239, 225, 0.15)",
  borderTopColor: "rgba(255, 244, 236, 0.28)",
  borderBottomColor: "rgba(255, 225, 207, 0.10)",
  backgroundColor: "transparent",
  overflow: "hidden",
};
export const GLASS_CONTROL_INPUT_STYLE: TextStyle = {
  paddingVertical: 14,
  paddingHorizontal: 16,
  fontSize: 18,
  color: "rgba(255, 255, 255, 0.94)",
  fontFamily: "DMSans_400Regular",
  textAlign: "center",
};

function fixtureDisablesMotion() {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_splash_motion") ===
    "off"
  );
}

if (Platform.OS === "web" && typeof document !== "undefined") {
  const id = "louise-moving-gradient-title-styles";
  if (!document.getElementById(id)) {
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes inviteTitleShimmer {
        0% { background-position: 200% 200%; }
        50% { background-position: 0% 0%; }
        100% { background-position: 200% 200%; }
      }
      .louise-moving-gradient-title {
        animation: inviteTitleShimmer 6s ease-in-out infinite;
      }
      .louise-moving-gradient-title--static {
        animation: none;
        background-position: 200% 200%;
      }
      @media (prefers-reduced-motion: reduce) {
        .louise-moving-gradient-title {
          animation: none;
          background-position: 200% 200%;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

type Props = {
  children: React.ReactNode;
  bottomPadding?: number;
};

export default function OnboardingLayout({ children, bottomPadding }: Props) {
  const { width, height } = useWindowDimensions();
  const { compact, condensed } = getOnboardingViewport(width, height);
  const motionDisabled = fixtureDisablesMotion();
  const contentOpacity = useRef(
    new Animated.Value(motionDisabled ? 1 : 0),
  ).current;
  const contentOffset = useRef(
    new Animated.Value(motionDisabled ? 0 : 7),
  ).current;

  useEffect(() => {
    if (motionDisabled) return;

    const entrance = Animated.parallel([
      Animated.timing(contentOpacity, {
        toValue: 1,
        duration: 170,
        useNativeDriver: true,
      }),
      Animated.timing(contentOffset, {
        toValue: 0,
        duration: 170,
        useNativeDriver: true,
      }),
    ]);
    entrance.start();
    return () => entrance.stop();
  }, [contentOffset, contentOpacity, motionDisabled]);

  return (
    <OnboardingShell>
      <Animated.View
        style={{
          flex: 1,
          width: "100%",
          maxWidth: 480,
          alignSelf: "center",
          opacity: contentOpacity,
          transform: [{ translateY: contentOffset }],
        }}
      >
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingHorizontal: condensed ? 16 : 24,
            paddingBottom:
              bottomPadding ?? (compact ? 16 : condensed ? 24 : 40),
          }}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </ScrollView>
      </Animated.View>
    </OnboardingShell>
  );
}

/**
 * The warm moving title treatment used by Louise invitation surfaces.
 * Native retains the readable white fallback used by those surfaces.
 */
export function MovingGradientTitle({
  className,
  style,
  ...props
}: MovingGradientTitleProps) {
  const webClassName = [
    className,
    "louise-moving-gradient-title",
    fixtureDisablesMotion() && "louise-moving-gradient-title--static",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Text
      {...props}
      className={Platform.OS === "web" ? webClassName : className}
      style={[
        { color: "#FFFFFF" },
        style,
        Platform.OS === "web" && WEB_GRADIENT_STYLE,
      ]}
    />
  );
}

export function LouiseMark({
  accessibilityLabel = "Louise",
  size,
}: {
  accessibilityLabel?: string;
  size: number;
}) {
  return (
    <Image
      accessibilityLabel={accessibilityLabel}
      source={LOUISE_MARK}
      resizeMode="contain"
      style={{ width: size, height: size }}
    />
  );
}

export function ScreenTitle({ children }: { children: React.ReactNode }) {
  const { width, height } = useWindowDimensions();
  const { compact, condensed } = getOnboardingViewport(width, height);

  return (
    <MovingGradientTitle
      style={{
        fontFamily: "Tobias_300Light",
        fontSize: compact ? 28 : condensed ? 30 : 32,
        lineHeight: compact ? 31 : condensed ? 33 : 36,
        letterSpacing: -0.7,
        color: "#FFFFFF",
        textAlign: "center",
        marginTop: compact ? 12 : condensed ? 18 : 24,
        marginBottom: compact ? 6 : 10,
      }}
    >
      {children}
    </MovingGradientTitle>
  );
}

export function ScreenSubtitle({ children }: { children: React.ReactNode }) {
  const { width, height } = useWindowDimensions();
  const { compact, condensed } = getOnboardingViewport(width, height);

  return (
    <Text
      className="font-dm-sans"
      style={{
        fontSize: 14,
        lineHeight: compact ? 18 : 20,
        color: "rgba(255, 255, 255, 0.72)",
        textAlign: "center",
        marginBottom: compact ? 12 : condensed ? 16 : 20,
      }}
    >
      {children}
    </Text>
  );
}

export function ChoiceGlassBackdrop({
  borderRadius = 17,
  tint = "dark",
}: {
  borderRadius?: number;
  tint?: "dark" | "default";
}) {
  return (
    <>
      <BlurView
        pointerEvents="none"
        intensity={28}
        tint={tint}
        experimentalBlurMethod="dimezisBlurView"
        style={[
          StyleSheet.absoluteFill,
          { zIndex: 0, borderRadius, overflow: "hidden" },
        ]}
      />
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFill,
          {
            zIndex: 0,
            borderRadius,
            backgroundColor: "rgba(255, 244, 236, 0.025)",
          },
        ]}
      />
    </>
  );
}

export function OnboardingPrimaryButton({
  title,
  onPress,
  disabled = false,
  showArrow = false,
  style,
}: {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  showArrow?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const capturePress = usePressCapture("OnboardingPrimaryButton");

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={() => {
        // The title arrives already translated (and swaps to a pending copy
        // mid-submit), so the funnel keys off the stable control identifier.
        capturePress(pressLabel(title), "onboarding_primary_button");
        onPress();
      }}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onHoverIn={() => {
        if (!disabled) setHovered(true);
      }}
      onHoverOut={() => setHovered(false)}
      style={[
        {
          width: "100%",
          alignSelf: "stretch",
          minHeight: 58,
          borderRadius: 29,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          paddingHorizontal: 24,
          overflow: "hidden",
          backgroundColor: pressed ? "#F1F1F1" : "#FFFFFF",
          opacity: disabled ? 0.42 : pressed ? 0.9 : 1,
          transform: [
            {
              translateY: hovered && !pressed && !disabled ? -1 : 0,
            },
            {
              scale:
                pressed && !disabled ? 0.985 : hovered && !disabled ? 1.006 : 1,
            },
          ],
          ...(Platform.OS !== "android"
            ? {
                shadowColor: "#000000",
                shadowOffset: {
                  width: 0,
                  height: hovered && !disabled ? 11 : 8,
                },
                shadowOpacity: hovered && !disabled ? 0.2 : 0.14,
                shadowRadius: hovered && !disabled ? 22 : 18,
              }
            : {}),
        },
        Platform.OS === "web"
          ? ({
              transition:
                "background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, opacity 160ms ease",
              cursor: disabled ? "default" : "pointer",
            } as ViewStyle)
          : null,
        style,
      ]}
    >
      <Text
        className="font-dm-sans"
        style={{
          color: "#1C130F",
          fontFamily: "DMSans_500Medium",
          fontSize: 16,
          lineHeight: 22,
          fontWeight: "500",
          textAlign: "center",
        }}
      >
        {title}
      </Text>
      {showArrow && (
        <View
          style={[
            {
              transform: [{ translateX: hovered && !disabled ? 2 : 0 }],
            },
            Platform.OS === "web"
              ? ({
                  transition: "transform 160ms ease",
                } as ViewStyle)
              : null,
          ]}
        >
          <ArrowRight size={18} color="#1C130F" strokeWidth={2} />
        </View>
      )}
    </Pressable>
  );
}

export function ContinueButton({
  title,
  onPress,
  disabled,
}: {
  title?: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  const { width, height } = useWindowDimensions();
  const { compact, condensed } = getOnboardingViewport(width, height);
  const t = useTranslations();
  return (
    <View style={{ marginTop: compact ? 16 : condensed ? 20 : 24 }}>
      <OnboardingPrimaryButton
        title={title ?? t("onboarding.continue")}
        onPress={onPress}
        disabled={disabled}
        showArrow
      />
    </View>
  );
}
